/**
 * Creator / Host Spotlight questionnaire definitions.
 *
 * Shared by the public questionnaire pages, the public Spotlight (hall of fame)
 * pages, and the admin panel so questions and answers always line up.
 *
 * Field types:
 *  - text      single-line input
 *  - textarea  multi-line input
 *  - select    dropdown, `options` = [{ value, label }]
 *  - list      three short inputs (stored as an array)
 *
 * Answers are stored in `spotlight_submissions.answers` as { [id]: value }.
 * Name / IG handle / email / uploads live in dedicated columns and are handled
 * separately by the form.
 */

const MEMBER_LENGTH_OPTIONS = [
  { value: 'under_3_months', label: 'Just joined (under 3 months)' },
  { value: '3_6_months', label: '3–6 months' },
  { value: '6_12_months', label: '6–12 months' },
  { value: '1_2_years', label: '1–2 years' },
  { value: '2_plus_years', label: '2+ years' },
];

const FULL_TIME_OPTIONS = [
  { value: 'full_time', label: 'Yes, this is my full-time job' },
  { value: 'part_time', label: 'Part-time / side hustle' },
  { value: 'working_toward', label: 'Not yet, but working toward it' },
];

const COFFEE_OPTIONS = [
  { value: 'black', label: 'Black' },
  { value: 'cream_sugar', label: 'Cream & sugar' },
  { value: 'latte', label: 'Latte / cappuccino' },
  { value: 'iced', label: 'Iced' },
  { value: 'cold_brew', label: 'Cold brew' },
  { value: 'espresso', label: 'Espresso, straight up' },
  { value: 'decaf', label: 'Decaf' },
  { value: 'tea', label: "I'm a tea person" },
  { value: 'none', label: "I don't drink coffee" },
];

const UPCOMING_EVENT_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'Not right now' },
];

const CHANNELS_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

/**
 * Build the shared, mostly-identical question set with a few type-specific
 * wording tweaks.
 */
function buildQuestions(type) {
  const isHost = type === 'host';
  const role = isHost ? 'hosting' : 'content creation';

  return [
    {
      section: 'Your story',
      questions: [
        {
          id: 'full_time',
          label: isHost ? 'Is short-term rental hosting your full-time job?' : 'Is content creation your full-time job?',
          type: 'select',
          options: FULL_TIME_OPTIONS,
          required: true,
        },
        {
          id: 'got_started',
          label: `How did you get started in ${isHost ? 'short-term rental hosting' : role}?`,
          type: 'textarea',
          required: true,
          placeholder: 'Tell us the origin story.',
        },
        {
          id: 'member_length',
          label: 'How long have you been a member of Postaway?',
          type: 'select',
          options: MEMBER_LENGTH_OPTIONS,
          required: true,
        },
        {
          id: 'memorable_story',
          label: isHost
            ? "What's the most memorable guest experience or hosting story you have, and why?"
            : "What's the most memorable hospitality collab you've done in the past, and why?",
          hint: isHost
            ? 'A special request, a guest who became a repeat, a moment that reminded you why you love this.'
            : "Host, amenities, location, etc. If you haven't completed a hospitality collab yet, tell us: what's one compelling thing about you that would convince a host to partner with you?",
          type: 'textarea',
          required: true,
        },
        {
          id: 'hometown',
          label: "Where's your hometown?",
          type: 'text',
          required: true,
          placeholder: 'City, State / Country',
        },
        {
          id: 'love_hospitality',
          label: 'Why do you love the hospitality space?',
          type: 'textarea',
          required: true,
        },
        {
          id: 'fun_fact',
          label: "What's a fun or surprising fact about you that has nothing to do with STRs?",
          hint: isHost ? undefined : 'Hobby, past career, hidden talent — the "human" hook.',
          type: 'textarea',
          required: true,
        },
        {
          id: 'next_goal',
          label: "What's your next big goal?",
          hint: isHost
            ? 'A new property, a renovation, hitting a new revenue milestone?'
            : 'More collabs, a content milestone, or a new project you\u2019re launching?',
          type: 'textarea',
          required: true,
        },
        {
          id: 'advice',
          label: isHost
            ? "What's one piece of advice you'd give someone thinking about becoming an STR host?"
            : "What's one piece of advice you'd give a creator wanting to break into this space?",
          type: 'textarea',
          required: true,
        },
        {
          id: 'community_connect',
          label: isHost
            ? "Who's someone in the Postaway community you'd love to connect with or learn from?"
            : "Who's someone in the Postaway community you'd love to collaborate with, and on what?",
          type: 'textarea',
          required: false,
          placeholder: '@handle and a sentence or two',
        },
      ],
    },
    {
      section: 'Just for fun',
      questions: [
        {
          id: 'bucket_list',
          label: "What's your bucket list destination?",
          type: 'text',
          required: true,
        },
        {
          id: 'island_food',
          label: 'If you were stranded on an island and could only have one type of food, what would it be?',
          type: 'text',
          required: true,
        },
        {
          id: 'coffee',
          label: 'How do you like your coffee?',
          type: 'select',
          options: COFFEE_OPTIONS,
          required: true,
        },
        {
          id: 'top_apps',
          label: "List the top three apps you can't live without.",
          type: 'list',
          count: 3,
          required: true,
        },
      ],
    },
    {
      section: 'Community & plugs',
      questions: [
        {
          id: 'upcoming_event',
          label: 'Do you have a group trip, retreat, webinar, or meetup coming up that other members could join or learn from?',
          type: 'select',
          options: UPCOMING_EVENT_OPTIONS,
          required: true,
        },
        {
          id: 'upcoming_event_details',
          label: 'Tell us about it',
          hint: 'What is it, when, and where can members sign up or learn more?',
          type: 'textarea',
          required: false,
          showWhen: { id: 'upcoming_event', equals: 'yes' },
        },
        {
          id: 'channels',
          label: isHost
            ? 'Are you running a Substack, newsletter, blog, or podcast about hosting we should be pointing members toward?'
            : 'Are you running a Substack, newsletter, YouTube channel, or podcast we should be pointing members toward?',
          type: 'select',
          options: CHANNELS_OPTIONS,
          required: true,
        },
        {
          id: 'channels_details',
          label: 'Share the links',
          type: 'textarea',
          required: false,
          placeholder: 'Name + link, one per line',
          showWhen: { id: 'channels', equals: 'yes' },
        },
        {
          id: 'next_spotlight',
          label: isHost
            ? 'Who would you most like to see in the Host Spotlight next?'
            : 'Who would you most like to see in the Creator Spotlight next?',
          type: 'text',
          required: false,
          placeholder: '@handle',
        },
      ],
    },
  ];
}

export const SPOTLIGHT_SECTIONS = {
  creator: buildQuestions('creator'),
  host: buildQuestions('host'),
};

/** Flat list of questions for a type (handy for admin rendering / validation). */
export function getSpotlightQuestions(type) {
  const sections = SPOTLIGHT_SECTIONS[type] || SPOTLIGHT_SECTIONS.creator;
  return sections.flatMap((s) => s.questions);
}

/** Resolve the human-readable label for a stored answer. */
export function formatSpotlightAnswer(question, value) {
  if (value == null || value === '') return '';
  if (question.type === 'select') {
    const opt = (question.options || []).find((o) => o.value === value);
    return opt ? opt.label : String(value);
  }
  if (question.type === 'list') {
    return Array.isArray(value) ? value.filter(Boolean).join(', ') : String(value);
  }
  return String(value);
}

/** Should this question be displayed given the current answers? */
export function isSpotlightQuestionVisible(question, answers) {
  if (!question.showWhen) return true;
  return (answers || {})[question.showWhen.id] === question.showWhen.equals;
}

export const SPOTLIGHT_TYPE_LABELS = {
  creator: 'Creator',
  host: 'Host',
};

export const SPOTLIGHT_STATUS_LABELS = {
  new: 'New',
  scheduled: 'Scheduled',
  published: 'Published',
  archived: 'Archived',
};
